import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js/pure';
import type { Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { apiClient } from '../services/api';
import { useAuth } from './AuthContext';

interface StripeContextType {
  stripe: Stripe | null;
  publishableKey: string | null;
  isLoading: boolean;
}

const StripeContext = createContext<StripeContextType | undefined>(undefined);

export function StripeProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const ownerId = user?.id || null;
  const [configuration, setConfiguration] = useState<StripeContextType & { ownerId: string | null }>({
    ownerId: null, stripe: null, publishableKey: null, isLoading: true,
  });

  useEffect(() => {
    const isPlatformHost =
      typeof window !== 'undefined' &&
      window.location?.hostname?.toLowerCase().startsWith('globaladmin.');

    let cancelled = false;
    const update = (value: StripeContextType) => {
      if (!cancelled) setConfiguration({ ownerId, ...value });
    };
    const unavailable = { stripe: null, publishableKey: null, isLoading: false };

    if (authLoading) {
      update({ ...unavailable, isLoading: true });
      return () => { cancelled = true; };
    }

    const isBookingCapture = /^\/(reserve|inquire|booking-chat)\/?$/.test(window.location.pathname);
    if (isPlatformHost || isBookingCapture || !user) {
      update(unavailable);
      return () => { cancelled = true; };
    }

    update({ ...unavailable, isLoading: true });
    void (async () => {
      try {
        const response = await apiClient.get('/payments/config');
        if (cancelled) return;
        const { publishableKey: key } = response.data;
        if (!key) {
          update(unavailable);
          return;
        }
        const stripe = await loadStripe(key);
        update({ stripe, publishableKey: key, isLoading: false });
      } catch (error: any) {
        if (cancelled) return;
        const status = error?.response?.status;
        // Stripe is optional and only configured for authenticated company tenants.
        if (status !== 401 && status !== 403 && status !== 404) {
          console.warn('Stripe configuration not available - payment features disabled');
        }
        update(unavailable);
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, ownerId]);

  // Keep the application mounted as optional payment configuration resolves.
  // A different authenticated identity starts Elements with no previous Stripe instance.
  const value: StripeContextType = configuration.ownerId === ownerId
    ? configuration
    : { stripe: null, publishableKey: null, isLoading: authLoading || Boolean(user) };

  return (
    <StripeContext.Provider value={value}>
      <Elements key={ownerId || 'anonymous'} stripe={value.stripe}>
        {children}
      </Elements>
    </StripeContext.Provider>
  );
}

export function useStripe() {
  const context = useContext(StripeContext);
  if (context === undefined) {
    throw new Error('useStripe must be used within a StripeProvider');
  }
  return context;
}
